import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  inicializarMaterias,
  STATUS,
  aprobarCursadasAnterioresRecursivo,
  recalcularEstadoMaterias,
  calcularCursadasAprobadasCPC,
  // calcularFinalesAprobadosCPC,
  calcularPuntosCPO,
  isCBCCompleto,
  calcularPuntosCPOGenerico
} from '../utils/careerLogic';

export const useMaterias = (planCarreraAbogacia, orientacionSeleccionadaId = '') => {
  const [materias, setMaterias] = useState(() => {
    const materiasGuardadas = localStorage.getItem('materias');
    if (materiasGuardadas) {
      const materiasParseadas = JSON.parse(materiasGuardadas);
      // Migración: FINAL_APROBADO -> CURSADA_APROBADA
      Object.values(materiasParseadas).forEach((m) => {
        if (m && m.estado === 'FINAL_APROBADO') {
          m.estado = STATUS.CURSADA_APROBADA;
        }
      });
      return materiasParseadas;
    }
    const materiasIniciales = inicializarMaterias(planCarreraAbogacia);
    return materiasIniciales; 
  });

  // Flag compacto para CBC aprobado
  const [cbcAprobado, setCbcAprobado] = useState(() => {
    const saved = localStorage.getItem('cbcAprobado');
    if (saved !== null) return JSON.parse(saved);
    // Migración suave: inferir del estado de materias guardadas (si existen)
    try {
      const materiasGuardadas = localStorage.getItem('materias');
      if (materiasGuardadas) {
        const parsed = JSON.parse(materiasGuardadas);
        return isCBCCompleto(parsed, false);
      }
    } catch (_) { /* no-op */ }
    return false;
  });

  // Persistencia
  useEffect(() => {
    localStorage.setItem('materias', JSON.stringify(materias));
  }, [materias]);

  useEffect(() => {
    localStorage.setItem('cbcAprobado', JSON.stringify(cbcAprobado));
    // Persistencia adicional compatible: app_flags_v1
    try {
      const existing = localStorage.getItem('app_flags_v1');
      const flags = existing ? JSON.parse(existing) : {};
      flags.cbcAprobado = cbcAprobado;
      localStorage.setItem('app_flags_v1', JSON.stringify(flags));
    } catch (_) { /* no-op */ }
  }, [cbcAprobado]);

  const reiniciarProgreso = useCallback(() => {
    // Inicializar y recalcular estados habilitados desde cero
    const materiasIniciales = inicializarMaterias(planCarreraAbogacia);
    const cpcCursadasCount = calcularCursadasAprobadasCPC(materiasIniciales);
    const cpcFinalesCount = 0; // FINAL_APROBADO eliminado
    const cpoPuntos = calcularPuntosCPO(materiasIniciales, orientacionSeleccionadaId);
    const materiasRecalculadas = recalcularEstadoMaterias(
      materiasIniciales,
      cpcCursadasCount,
      cpcFinalesCount,
      cpoPuntos,
      false // Reset: CBC no aprobado
    );
    setMaterias(materiasRecalculadas);
    setCbcAprobado(false);
    try {
      localStorage.setItem('cbcAprobado', JSON.stringify(false));
      const existing = localStorage.getItem('app_flags_v1');
      const flags = existing ? JSON.parse(existing) : {};
      flags.cbcAprobado = false;
      localStorage.setItem('app_flags_v1', JSON.stringify(flags));
    } catch (_) { /* no-op */ }
    localStorage.removeItem('materias');
  }, [planCarreraAbogacia, orientacionSeleccionadaId]);

  const updateMateriaEstado = useCallback(
    (materiaId, nuevoEstado, extra = {}) => {
      let nuevasMateriasTemporal = JSON.parse(JSON.stringify(materias));
      const materiaActual = nuevasMateriasTemporal[materiaId];
      if (!materiaActual) return materias;

      nuevasMateriasTemporal[materiaId].estado = nuevoEstado;
      if (Object.prototype.hasOwnProperty.call(extra, 'notaFinal')) {
        nuevasMateriasTemporal[materiaId].notaFinal = extra.notaFinal;
      }
      if (Object.prototype.hasOwnProperty.call(extra, 'fecha')) {
        nuevasMateriasTemporal[materiaId].fecha = extra.fecha;
      }

      if (nuevoEstado === STATUS.CURSADA_APROBADA) {
        aprobarCursadasAnterioresRecursivo(
          materiaId,
          nuevasMateriasTemporal,
          planCarreraAbogacia
        );
      }

      // Recalcular contadores y puntos con el estado temporal
      const cpcCursadasCount = calcularCursadasAprobadasCPC(nuevasMateriasTemporal);
      const cpcFinalesCount = 0; // FINAL_APROBADO eliminado
      const cpoPuntos = calcularPuntosCPO(
        nuevasMateriasTemporal,
        orientacionSeleccionadaId
      );

      const materiasRecalculadas = recalcularEstadoMaterias(
        nuevasMateriasTemporal,
        cpcCursadasCount,
        cpcFinalesCount,
        cpoPuntos,
        cbcAprobado
      );

      setMaterias(materiasRecalculadas);
      return materiasRecalculadas;
    },
    [materias, orientacionSeleccionadaId, planCarreraAbogacia, cbcAprobado]
  );

  const updateNotaFecha = useCallback(
    (materiaId, nota, fecha = null) => {
      const nuevas = { ...materias };
      if (nuevas[materiaId]) {
        if (typeof nota !== 'undefined') {
          nuevas[materiaId].notaFinal = nota;
        }
        if (typeof fecha !== 'undefined') {
          nuevas[materiaId].fecha = fecha;
        }
      }
      setMaterias(nuevas);
      return nuevas;
    },
    [materias]
  );

  // Acción: aprobar CBC compacto
  const aprobarCBC = useCallback(() => {
    setMaterias(prev => {
      const nuevas = JSON.parse(JSON.stringify(prev));
      ['cbc_1','cbc_2','cbc_3','cbc_4','cbc_5','cbc_6'].forEach(id => {
        if (nuevas[id]) {
          nuevas[id].estado = STATUS.CURSADA_APROBADA;
          nuevas[id].notaFinal = -3;
        }
      });
      const cpcCursadasCount = calcularCursadasAprobadasCPC(nuevas);
      const cpcFinalesCount = 0; // FINAL_APROBADO eliminado
      const cpoPuntos = calcularPuntosCPO(nuevas, orientacionSeleccionadaId);
      const recalc = recalcularEstadoMaterias(nuevas, cpcCursadasCount, cpcFinalesCount, cpoPuntos, true);
      return recalc;
    });
    setCbcAprobado(true);
  }, [orientacionSeleccionadaId]);

  // Acción: reset CBC (debug)
  const resetCBC = useCallback(() => {
    setMaterias(prev => {
      const nuevas = JSON.parse(JSON.stringify(prev));
      ['cbc_1','cbc_2','cbc_3','cbc_4','cbc_5','cbc_6'].forEach(id => {
        if (nuevas[id]) {
          nuevas[id].estado = STATUS.NO_CURSADA;
          nuevas[id].notaFinal = -1;
        }
      });
      const cpcCursadasCount = calcularCursadasAprobadasCPC(nuevas);
      const cpcFinalesCount = 0; // FINAL_APROBADO eliminado
      const cpoPuntos = calcularPuntosCPO(nuevas, orientacionSeleccionadaId);
      const recalc = recalcularEstadoMaterias(nuevas, cpcCursadasCount, cpcFinalesCount, cpoPuntos, false);
      return recalc;
    });
    setCbcAprobado(false);
  }, [orientacionSeleccionadaId]);

  // Contadores derivados y puntos (expuestos por conveniencia)
  const cpcCursadas = useMemo(() => calcularCursadasAprobadasCPC(materias), [materias]);
  const cpoPuntosGenericos = useMemo(() => calcularPuntosCPOGenerico(materias), [materias]);

  // Alias para coincidir con snippets
  const resetProgress = useCallback(() => {
    reiniciarProgreso();
    try {
      localStorage.setItem('app_flags_v1', JSON.stringify({ cbcAprobado: false }));
    } catch (_) { /* no-op */ }
  }, [reiniciarProgreso]);

  // Flags API compatible (solo cbcAprobado por ahora)
  const flags = useMemo(() => ({ cbcAprobado }), [cbcAprobado]);
  const setFlags = useCallback((next) => {
    if (next && Object.prototype.hasOwnProperty.call(next, 'cbcAprobado')) {
      setCbcAprobado(Boolean(next.cbcAprobado));
    }
  }, []);

  return {
    materias,
    setMaterias,
    cbcAprobado,
    setCbcAprobado,
    reiniciarProgreso,
    resetProgress,
    updateMateriaEstado,
    updateNotaFecha,
    aprobarCBC,
    resetCBC,
    // Expuestos para barra superior/estadísticas
    cpcCursadas,
    cpoPuntos: cpoPuntosGenericos,
    // API de flags simple
    flags,
    setFlags
  };
};
